import { appInsightLogger, checkIsSuperUser } from './../../KonnectAnalytics/konnectAnalytics';
import axios, {
  Method,
  AxiosRequestConfig,
  CancelToken,
  CancelTokenSource,
  AxiosResponse,
} from 'axios';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { PayloadAC } from 'typesafe-actions';

import { Store } from 'store/storeTypes';
import { authService } from 'services/AuthProvider';
import { makeAuthHeader } from './utils';
import handleErrorAction from './handleErrorAction';
import { sendMetric } from '../../logger/logger';
import config from 'config/config';
import * as Sentry from '@sentry/react';
export interface ApiRequest {
  url: string | ((state: Store) => string);
  method?: Method;
  data?: object | ((state: Store) => object);
  headers?: any;
  params?: any;
  responseType?: ResponseType;
  credentials?: string;
}

export interface ApiActionLogic {
  onStarted: (dispatch: ThunkDispatch<Store, any, Action>, state: Store, data: any) => void;
  onSuccess: (
    data: any,
    dispatch: ThunkDispatch<Store, any, Action>,
    state: Store,
    status: number
  ) => void;
  onFailed: (error: any, dispatch: ThunkDispatch<Store, any, Action>, state: Store) => void;
}

export interface InterceptorFunc {
  (state: Store): boolean;
}

export interface ApiActionConfig {
  request: ApiRequest;
  logic: ApiActionLogic;
  interceptor?: InterceptorFunc;
  requireAuth?: boolean;
  // By default, a new request to the same URL cancels any request to that same URL still
  // in flight (dedup by URL). Set true to let multiple requests to this URL run at once —
  // e.g. firing several single-item POSTs to the same endpoint in parallel.
  allowConcurrentRequests?: boolean;
}

const pendingRequests = new Map<string, CancelTokenSource>();
const noNeedToCancelEndpointsArray = ['current-state'];

// Function to handle HTTP errors and send them to Sentry
const sendHttpErrorToSentry = (error: any, request: ApiRequest, state: Store) => {
  try {
    const isAxiosError = error.response !== undefined;

    if (isAxiosError) {
      const status = error.response?.status;
      const statusText = error.response?.statusText;
      const url = typeof request.url === 'string' ? request.url : 'dynamic_url';
      const method = request.method || 'GET';

      // Send to Sentry only errors with codes 401, 404, 500+
      if (status === 401 || status === 404 || status >= 500) {
        // Create specific error messages for better grouping
        const errorMessage = `${method} ${url} returned ${status} ${statusText}`;

        Sentry.captureException(new Error(errorMessage), {
          tags: {
            httpStatus: String(status),
            httpMethod: method,
            endpoint: url,
            errorType: 'http_error',
          } as Record<string, string>,
          contexts: {
            request: {
              url: url,
              method: method,
              status_code: status,
              status_text: statusText,
            },
            user: {
              email: state.appUser?.userEmail || 'unknown',
              id: state.appUser?.id || 'unknown',
            },
            environment: {
              config_name: config.ENV_NAME || 'unknown',
            },
          } as Record<string, any>,
          extra: {
            responseData: error.response?.data,
            requestData: typeof request.data === 'object' ? request.data : 'dynamic_data',
            requestParams: request.params,
            timestamp: new Date().toISOString(),
          } as Record<string, any>,
          // Custom fingerprint to separate different HTTP error types
          fingerprint: [
            'http-error',
            String(status),
            method,
            url.replace(/\/\d+/g, '/{id}'), // normalize URLs with IDs
          ],
        });
      }
    }
  } catch (sentryError) {
    // If Sentry is not initialized or error occurred - just ignore
    console.warn('Failed to send HTTP error to Sentry:', sentryError);
  }
};

export default function apiAction(conf: ApiActionConfig) {
  const { request, logic, interceptor, requireAuth = true, allowConcurrentRequests = false } = conf;
  return async function(dispatch: ThunkDispatch<Store, any, Action>, getState: () => Store) {
    // Check authentication if required
    if (requireAuth) {
      const { isAuthenticated, idTokenPayload } = authService;
      if (!isAuthenticated() || !idTokenPayload || !idTokenPayload.sub) {
        return;
      }
    }

    let tokenSource: CancelTokenSource | undefined;
    const state = getState();
    const quieryString = generateQuieryString(request.params);
    const params = request.params;
    request.params = undefined;
    request.url = typeof request.url === 'function' ? request.url(state) : request.url;
    // baseUrl never carries the concurrency suffix — used for endpoint checks and metrics
    // so those stay identical regardless of allowConcurrentRequests.
    const baseUrl = request.url;
    const requestId = allowConcurrentRequests
      ? `${baseUrl}#${Date.now()}-${Math.random().toString(36).slice(2)}`
      : baseUrl;
    tokenSource = axios.CancelToken.source();
    const splittedBySlashUrl = baseUrl.split('/');
    const currentEndPoint = splittedBySlashUrl[splittedBySlashUrl.length - 1];
    // //*to set cookies
    if (currentEndPoint === 'portalUrl') {
      axios.defaults.withCredentials = false;
    } else {
      axios.defaults.withCredentials = true;
    }
    //*to cancel repeated request
    if (!allowConcurrentRequests && !noNeedToCancelEndpointsArray.includes(currentEndPoint)) {
      pendingRequests.get(requestId)?.cancel();
    }
    request.url += quieryString;
    request.data = typeof request.data === 'function' ? request.data(state) : request.data;
    request.method = request.method || 'GET';
    const authHeader = makeAuthHeader(authService);
    const contentTypeHeader = { 'Content-Type': 'application/x-www-form-urlencoded' };
    request.headers = request.headers
      ? { ...authHeader, ...contentTypeHeader, ...request.headers }
      : authHeader;
    //*in case we need to get blob -we use it in system page => dowload exeel report
    if (params && params.responseType) {
      request.responseType = params.responseType;
    }
    if (interceptor && interceptor(state)) return;
    logic.onStarted(dispatch, state, request.data);
    try {
      pendingRequests.set(requestId, tokenSource);
      const startTime = new Date().getTime();

      const response = await axios.request(({
        ...request,
        cancelToken: tokenSource?.token,
      } as unknown) as AxiosRequestConfig);
      sendMetric(baseUrl.substring(baseUrl.indexOf('api/') + 4), startTime, params);
      logic.onSuccess(response.data, dispatch, state, response.status);
      sendLogToAppInsight({ request, response, error: null, canceled: false, state });
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('request was canceled', request);
        sendLogToAppInsight({ request, error, canceled: true, state });
      } else {
        sendHttpErrorToSentry(error, request, state);
        logic.onFailed(error, dispatch, state);
        dispatch(handleErrorAction(error, `API: ${error.message}`));
        sendLogToAppInsight({ request, error, canceled: false, state });
      }
    } finally {
      tokenSource?.cancel();
      // Concurrent calls get a unique requestId per call (never reused/cancelled by a
      // sibling), so clean it up here instead of letting it sit in the map forever.
      if (allowConcurrentRequests) pendingRequests.delete(requestId);
    }
  };
}
type LogProps = {
  request: ApiRequest;
  response?: AxiosResponse<any>;
  error?: any;
  canceled: boolean;
  state: Store;
};
const sendLogToAppInsight = (props: LogProps) => {
  const { request, response, error, canceled, state } = props;
  const isSuperUser = authService.idTokenPayload[`${config.AUTH0_CLAIMS_NAMESPACE}is_super_user`];
  //value from server side comes not correct in case of superUser - so we need to fix it on client side  in case of super user the email will be in nickname field;
  const email = isSuperUser ? authService.idTokenPayload.nickname : authService.idTokenPayload.name;
  if (response) {
    appInsightLogger.trackEvent(
      { name: `http-response-success: ${request.url} from: ${email}` },
      {
        type: 'http-response-success',
        payload: response.data,
        endPoint: request.url,
        userEmail: state.appUser.userEmail,
        id: state.appUser.id,
        day: request?.params?.day,
        week: request?.params?.week,
        isSuperUser: checkIsSuperUser(authService),
      }
    );
  }
  if (error) {
    const email = isSuperUser
      ? authService.idTokenPayload.nickname
      : authService.idTokenPayload.name;
    appInsightLogger.trackEvent(
      {
        name: `http-response-${canceled ? 'canceled' : 'failed'}: ${request.url} from: ${email}`,
      },
      {
        type: `http-response-${canceled ? 'canceled' : 'failed'}`,
        payload: error,
        endPoint: request.url,
        userEmail: state.appUser.userEmail,
        id: state.appUser.id,
        day: request?.params?.day,
        week: request?.params?.week,
        isSuperUser: checkIsSuperUser(authService),
      }
    );
  }
};
const generateQuieryString = (params: any | undefined): string => {
  let quieryString = '';
  if (params) {
    quieryString = '?';
    Object.keys(params).forEach((key, index) => {
      if (params[key] !== undefined && params[key] !== null) {
        if (Array.isArray(params[key])) {
          params[key].forEach((param: string | number, i: number) => {
            quieryString += `${key}=${param}`;
            if (i !== params[key].length - 1) {
              quieryString += '&';
            }
          });
        } else if (params[key] instanceof Date) {
          quieryString += `${key}=${(params[key] as Date).toDateString()}`;
        } else if (typeof params[key] === 'object') {
          Object.keys(params[key]).forEach(innerKey => {
            if (Array.isArray(params[key][innerKey])) {
              params[key][innerKey].forEach((el: string | number, index: number) => {
                quieryString += `${key}.${innerKey}=${el}`;
                quieryString += '&';
              });
            } else {
              quieryString += `${key}.${innerKey}=${params[key][innerKey]}`;
              quieryString += '&';
            }
          });
          if (quieryString.charAt(quieryString.length - 1) === '&') {
            quieryString = quieryString.slice(0, quieryString.length - 1);
          }
        } else {
          quieryString += `${key}=${params[key]}`;
        }
        quieryString += '&';
      }
    });
  }
  if (quieryString.charAt(quieryString.length - 1) === '&') {
    quieryString = quieryString.slice(0, quieryString.length - 1);
  }
  return quieryString;
};

export interface ApiActionSimpleConfig {
  request: ApiRequest;
  actions: [any, PayloadAC<any, any>, any];
  interceptor?: InterceptorFunc;
  requireAuth?: boolean;
}

export const apiActionSimple = (conf: ApiActionSimpleConfig) => {
  const { request, actions, interceptor, requireAuth } = conf;

  return apiAction({
    request,
    logic: {
      onStarted: (dispatch, state, data = null) => {
        return dispatch(actions[0](data));
      },
      onSuccess: (data, dispatch) => {
        return dispatch(actions[1](data));
      },
      onFailed: (error, dispatch) => {
        return dispatch(actions[2](error));
      },
    },
    interceptor,
    requireAuth,
  });
};
